import { Button, Card, Descriptions, Form, Input, InputNumber, Space, Table } from "antd";
import classNames from "classnames/bind";
import style from '../index.module.scss'
import { useTranslation } from "react-i18next";
import { InputCurrency } from "components/";
import { formatCurrency } from "utils/";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CloseOutlined, EditOutlined, FileSyncOutlined, CloudUploadOutlined } from "@ant-design/icons";
import moment from "moment";
import { getDatesInRange } from "utils/date";
import { cloneDeep } from "lodash";
import revenueApi from "api/revenue";
import { formatPercent } from "utils/";
import { getUser } from "utils/";
import { roles } from "constants/";
import { renderHtmlTextBreakLine } from "utils/index";
import Table2XLSX from "./TableExport";
import ModalImport from "./ModalImport";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import { rolesEditBudget, rolesEditNote, rolesImport } from "../roles";

const cx = classNames.bind(style)
const EditableContext = createContext(null);
export default function RevenueTable({ shop, shopInfo, month, revenueInfo, listHoliday, notify, onSearch }) {
    const { t } = useTranslation()

    const [formImport] = Form.useForm();

    const [editMode, setEditMode] = useState(false)
    const [loading, setLoading] = useState(false)
    const [dataSource, setDataSource] = useState([])
    const [budget, setBudget] = useState(revenueInfo.budget)
    const [note, setNote] = useState(revenueInfo.note)
    const [openImport, setOpenImport] = useState(false)

    const handleSyncData = useCallback(() => {
        const startDateOfMonth = moment(month, 'YYYY-MM').startOf('month').format('YYYY-MM-DD')
        const endDateOfMonth = moment(month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')

        const rangeDate = getDatesInRange(startDateOfMonth, endDateOfMonth)

        setDataSource(rangeDate.map((date, idx) => {
            const index = revenueInfo?.list?.findIndex(item => moment(item.date).format('YYYY-MM-DD') === date)
            if (index !== -1) {
                return {
                    ...revenueInfo?.list[index],
                    date
                }
            }

            return {
                date
            }
        }))

        setBudget(revenueInfo.budget)
        setNote(revenueInfo.note)
    }, [month, revenueInfo])

    const onOpenImport = () => {
        setOpenImport(true)
    }

    const closeModalImport = () => {
        setOpenImport(false)
        formImport.resetFields()
    }

    useEffect(() => {
        handleSyncData()
    }, [handleSyncData, month, revenueInfo])

    const toggleModeEdit = () => {
        setEditMode(true)
    }

    const saveEdit = async () => {
        try {
            setLoading(true)
            await revenueApi.updateBudget({
                shop,
                month,
                amount: budget,
                note
            })
            await revenueApi.createRevenue({
                shop,
                month,
                list: dataSource
            })
            notify("success", t('notification'), t('updateRevenueSuccess'));
            onSearch()
            setEditMode(false)
        } catch (error) {
            notify("error", t('error'), error?.response?.data?.message || t('hasError'));
        } finally {
            setLoading(false)
        }
    }

    const cancelEdit = () => {
        setEditMode(false)
        handleSyncData()
    }

    const handleSave = useCallback((row) => {
        const newData = cloneDeep(dataSource);
        const index = newData.findIndex((item) => row.date === item.date);
        const item = newData[index];
        newData.splice(index, 1, {
            ...item,
            ...row,
        });
        setDataSource(newData);
    }, [dataSource]);

    const handleOnCell = useCallback((record, title, dataIndex, style) => {
        const isDayOff = !moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') == record.date)

        return {
            record,
            className: isDayOff && cx('dayoff'),
            editable: !isDayOff,
            dataIndex,
            title,
            handleSave,
            style
        }
    }, [handleSave, listHoliday])

    const EditableRow = ({ index, ...props }) => {
        const [form] = Form.useForm();
        return (
            <Form form={form} component={false}>
                <EditableContext.Provider value={form}>
                    <tr {...props} />
                </EditableContext.Provider>
            </Form>
        );
    };

    const EditableCell = ({
        title,
        editable,
        children,
        dataIndex,
        record,
        handleSave,
        ...restProps
    }) => {
        const [editing, setEditing] = useState(false);
        const inputRef = useRef(null);
        const form = useContext(EditableContext);
        useEffect(() => {
            if (editing) {
                inputRef.current.focus();
            }
        }, [editing]);
        const toggleEdit = () => {
            if (!editMode) return
            setEditing(!editing);
            form.setFieldsValue({
                [dataIndex]: record[dataIndex],
            });
        };
        const save = async (dataIndex) => {
            try {
                const values = await form.validateFields();
                toggleEdit();
                handleSave({
                    ...record,
                    ...values,
                });
            } catch (errInfo) {
                console.log('Save failed:', errInfo);
            }
        };

        const renderItem = () => {
            switch (dataIndex) {
                case 'note':
                    return <Input.TextArea rows={2} ref={inputRef} onPressEnter={() => save(dataIndex)} onBlur={() => save(dataIndex)} />
                default:
                    return <InputCurrency ref={inputRef} onPressEnter={() => save(dataIndex)} onBlur={() => save(dataIndex)} />
            }
        }
        let childNode = children;
        if (editable) {
            childNode = editing ? (
                <Form.Item
                    style={{
                        margin: 0,
                    }}
                    name={dataIndex}
                >
                    {renderItem()}
                </Form.Item>
            ) : (
                <div
                    className={cx({ 'editable-cell-value-wrap': editMode })}
                    onClick={toggleEdit}
                >
                    {children}
                </div>
            );
        }
        return <td {...restProps}>{childNode}</td>;
    };

    const columns = useMemo(() => [
        {
            title: t('revenueInMonth', { month }),
            className: cx('revenue-header', 'parent'),
            children: [
                {
                    title: t('day'),
                    dataIndex: "index",
                    key: "index",
                    align: 'right',
                    width: '4%',
                    onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                    render: (_, record, index) => index + 1,
                    onCell: (record) => ({
                        className: (!moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') === record.date)) && cx('dayoff')
                    })
                },
                {
                    title: t('dow'),
                    dataIndex: "date",
                    key: "date",
                    align: 'right',
                    width: '7%',
                    onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                    render: (_) => t(moment(_).format('dddd')),
                    onCell: (record) => ({
                        className: (!moment(record.date).weekday() || listHoliday.some(item => moment(item.date).format('YYYY-MM-DD') === record.date)) && cx('dayoff')
                    })
                },
                {
                    title: t('produce'),
                    onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                    children: [
                        {
                            title: t('shop'),
                            dataIndex: "morningShift",
                            key: "morningShift",
                            onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                            align: 'right',
                            width: '8%',
                            render: (_, record) => {
                                if (!record.morningShift && !record.noonShift) {
                                    return '　'
                                }

                                const morningShift = record.morningShift || 0
                                const noonShift = record.noonShift || 0

                                return noonShift < morningShift ? formatCurrency(morningShift + noonShift) : formatCurrency(noonShift)
                            },
                            onCell: (record) => handleOnCell(record, t('morningShift'), 'morningShift')
                        },
                        {
                            title: shopInfo?.cabinet_code_1 ? shopInfo.cabinet_code_1 : t('cabinet', { value: 1 }),
                            dataIndex: "produceCabinet1",
                            key: "produceCabinet1",
                            onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                            width: '8%',
                            align: 'right',
                            render: (_) => _ ? formatCurrency(_) : '　',
                            onCell: (record) => handleOnCell(record, shopInfo?.cabinet_code_1 ? shopInfo.cabinet_code_1 : t('cabinet', { value: 1 }), 'produceCabinet1'),
                            isHide: revenueInfo.cabinet < 1,
                        },
                        {
                            title: shopInfo?.cabinet_code_2 ? shopInfo.cabinet_code_2 : t('cabinet', { value: 2 }),
                            dataIndex: "produceCabinet2",
                            key: "produceCabinet2",
                            onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                            width: '8%',
                            align: 'right',
                            render: (_) => _ ? formatCurrency(_) : '　',
                            onCell: (record) => handleOnCell(record, shopInfo?.cabinet_code_2 ? shopInfo.cabinet_code_2 : t('cabinet', { value: 2 }), 'produceCabinet2'),
                            isHide: revenueInfo.cabinet < 2,
                        },
                    ]
                },
                {
                    title: t('revenue'),
                    onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                    children: [
                        {
                            title: t('shop'),
                            dataIndex: "revenue",
                            key: "revenue",
                            onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                            align: 'right',
                            width: '8%',
                            render: (_) => _ ? formatCurrency(_) : '　',
                            onCell: (record) => handleOnCell(record, t('revenue'), 'revenue')
                        },
                        {
                            title: shopInfo?.cabinet_code_1 ? shopInfo.cabinet_code_1 : t('cabinet', { value: 1 }),
                            dataIndex: "revenueCabinet1",
                            key: "revenueCabinet1",
                            onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                            width: '8%',
                            align: 'right',
                            render: (_) => _ ? formatCurrency(_) : '　',
                            onCell: (record) => handleOnCell(record, shopInfo?.cabinet_code_1 ? shopInfo.cabinet_code_1 : t('cabinet', { value: 1 }), 'revenueCabinet1'),
                            isHide: revenueInfo.cabinet < 1,
                        },
                        {
                            title: shopInfo?.cabinet_code_2 ? shopInfo.cabinet_code_2 : t('cabinet', { value: 2 }),
                            dataIndex: "revenueCabinet2",
                            key: "revenueCabinet2",
                            onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                            width: '8%',
                            align: 'right',
                            render: (_) => _ ? formatCurrency(_) : '　',
                            onCell: (record) => handleOnCell(record, shopInfo?.cabinet_code_2 ? shopInfo.cabinet_code_2 : t('cabinet', { value: 2 }), 'revenueCabinet2'),
                            isHide: revenueInfo.cabinet < 2,
                        },
                    ]
                },
                {
                    title: t('superMarket'),
                    dataIndex: "superMarket",
                    key: "superMarket",
                    onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                    align: 'right',
                    render: (_) => _ ? formatCurrency(_) : '　',
                    width: '7%',
                    onCell: (record) => handleOnCell(record, t('superMarket'), 'superMarket')
                },
                {
                    title: t('qsl'),
                    dataIndex: "qsl",
                    onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                    width: '8%',
                    align: 'right',
                    render: (_) => _ ? formatCurrency(_) : '　',
                    key: "qsl",
                    onCell: (record) => handleOnCell(record, t('qsl'), 'qsl')
                },
                {
                    title: t('cityFood'),
                    dataIndex: "cityFood",
                    onHeaderCell: (column) => ({ className: cx('revenue-header', 'child') }),
                    width: '8%',
                    align: 'right',
                    render: (_) => _ ? formatCurrency(_) : '　',
                    key: "cityFood",
                    onCell: (record) => handleOnCell(record, t('cityFood'), 'cityFood')
                },
            ]
        },
    ], [t, month, shopInfo.cabinet_code_1, shopInfo.cabinet_code_2, revenueInfo.cabinet, listHoliday, handleOnCell])

    const analytic = useMemo(() => {
        let totalMorningShift = 0;
        let totalNoonShift = 0;
        let produceShop = 0
        let produceCabin1 = 0
        let produceCabin2 = 0
        let revenueShop = 0
        let revenueCabin1 = 0
        let revenueCabin2 = 0
        let totalSuperMarket = 0
        let totalCityFood = 0
        let totalQsl = 0

        let numberOfWork = 0
        let netRevenue = 0
        let averageRevenue = 0
        let qslPercent = 0
        let cityFoodSuperMarket = 0
        let orderPercent = 0

        dataSource.forEach(({
            morningShift = 0,
            noonShift = 0,
            produceCabinet1 = 0,
            produceCabinet2 = 0,
            revenue = 0,
            revenueCabinet1 = 0,
            revenueCabinet2 = 0,
            superMarket = 0,
            cityFood = 0,
            qsl = 0,
        }) => {
            totalMorningShift += morningShift;
            totalNoonShift += noonShift;
            revenueShop += revenue;
            produceCabin1 += produceCabinet1;
            produceCabin2 += produceCabinet2;
            revenueCabin1 += revenueCabinet1;
            revenueCabin2 += revenueCabinet2;
            totalSuperMarket += superMarket;
            totalQsl += qsl;
            totalCityFood += cityFood;
            produceShop += (noonShift < morningShift ? morningShift + noonShift : noonShift)

            if (morningShift || noonShift || (revenueInfo.cabinet > 0 && produceCabinet1) || (revenueInfo.cabinet > 1 && produceCabinet2)) {
                numberOfWork++
            }
        });

        netRevenue = (revenueShop + revenueCabin1 + revenueCabin2) * .93
        averageRevenue = numberOfWork ? netRevenue / numberOfWork : 0
        qslPercent = netRevenue ? totalQsl / netRevenue : 0
        cityFoodSuperMarket = netRevenue ? (totalSuperMarket + totalCityFood) / netRevenue : 0
        orderPercent = qslPercent + cityFoodSuperMarket

        return {
            totalMorningShift,
            totalNoonShift,
            produceShop,
            produceCabin1,
            produceCabin2,
            revenueShop,
            revenueCabin1,
            revenueCabin2,
            totalSuperMarket,
            totalCityFood,
            totalQsl,
            totalBalance: budget - (totalSuperMarket + totalQsl + totalCityFood),
            numberOfWork,
            netRevenue,
            averageRevenue,
            qslPercent,
            cityFoodSuperMarket,
            orderPercent,
            growth: revenueInfo?.averageRevenueLastYear ? ((averageRevenue - revenueInfo?.averageRevenueLastYear) / revenueInfo?.averageRevenueLastYear) : 0
        }
    }, [dataSource, budget, revenueInfo])

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };

    const onChangeBudget = (e) => {
        setBudget(e.target.value)
    }

    const onChangeNote = (e) => {
        setNote(e.target.value)
    }

    const onImport = async (values) => {
        try {
            const reader = new FileReader();
            const rABS = !!reader.readAsBinaryString;
            reader.onload = async (e) => {
                /* Parse data */
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
                /* Get first worksheet */
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                /* Convert array of arrays */
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                if (Array.isArray(data) && data.length >= 4) {
                    let payload = []
                    let error = ''
                    data.filter(item => item.length > 0 && item).forEach((item, idx) => {
                        if (idx > 2) {
                            if (item.length > (13 + (revenueInfo.cabinet || 0) * 2)) {
                                error = t('wrongFileFormat')
                            }
                            if (!revenueInfo.cabinet) {
                                const [
                                    countDate,
                                    dow,
                                    morningShift,
                                    revenue,
                                    superMarket,
                                    qsl,
                                    cityFood,
                                ] = item

                                payload.push({
                                    date: moment(month, 'YYYY-MM').date(countDate).format('YYYY-MM-DD'),
                                    morningShift,
                                    revenue,
                                    superMarket,
                                    qsl,
                                    cityFood,
                                })

                            } else if (revenueInfo.cabinet > 1) {
                                const [
                                    countDate,
                                    dow,
                                    morningShift,
                                    produceCabinet1,
                                    produceCabinet2,
                                    revenue,
                                    revenueCabinet1,
                                    revenueCabinet2,
                                    superMarket,
                                    qsl,
                                    cityFood,
                                ] = item
                                payload.push({
                                    date: moment(month, 'YYYY-MM').date(countDate).format('YYYY-MM-DD'),
                                    morningShift,
                                    produceCabinet1,
                                    produceCabinet2,
                                    revenue,
                                    revenueCabinet1,
                                    revenueCabinet2,
                                    superMarket,
                                    qsl,
                                    cityFood,
                                })
                            } else {
                                const [
                                    countDate,
                                    dow,
                                    morningShift,
                                    produceCabinet1,
                                    revenue,
                                    revenueCabinet1,
                                    superMarket,
                                    qsl,
                                    cityFood,
                                ] = item
                                payload.push({
                                    date: moment(month, 'YYYY-MM').date(countDate).format('YYYY-MM-DD'),
                                    morningShift,
                                    produceCabinet1,
                                    revenue,
                                    revenueCabinet1,
                                    superMarket,
                                    qsl,
                                    cityFood,
                                })
                            }
                        }
                    })
                    if (!error) {
                        try {
                            await revenueApi.createRevenue({
                                shop,
                                month,
                                list: payload
                            })
                            notify("success", t('notification'), t('importSuccess'));
                            onSearch()
                            closeModalImport()
                        } catch (error) {
                            notify("error", t('error'), t('hasError'));
                        }
                    } else {
                        notify("error", t('error'), error || t('hasError'));
                    }

                } else toast.error("Import error!")

                /* Update state */
            };
            if (rABS) reader.readAsBinaryString(values.upload[0].originFileObj);
            else reader.readAsArrayBuffer(values.upload[0].originFileObj);
        } catch (error) {
            // toast.error(error?.response?.data || ERROR_OCCUR)
        } finally {
        }
    }

    return (
        <>
            <ModalImport loading={loading} show={openImport} onFinish={onImport} form={formImport} onCancel={closeModalImport} />
            <Card>
                <div className={cx('button-area')}>
                    {
                        !editMode && <Button icon={<EditOutlined />} type='primary' size='large' onClick={toggleModeEdit}>{t('edit')}</Button>
                    }
                    {
                        editMode &&
                        <Space>
                            <Button loading={loading} icon={<FileSyncOutlined />} type='primary' size='large' onClick={saveEdit}>{t('save')}</Button>
                            <Button loading={loading} icon={<CloseOutlined />} danger size='large' onClick={cancelEdit}>{t('cancel')}</Button>
                        </Space>
                    }
                    {
                        (rolesImport.includes(+getUser().role)) &&
                        <Space>
                            <Button size="large" icon={<CloudUploadOutlined />} onClick={onOpenImport} >
                                {t('import')}
                            </Button>
                            <Table2XLSX {...{ month, revenueInfo, dataSource, shopInfo }} />
                        </Space>
                    }

                </div>

                <div className={cx('budget-area')}>
                    <Descriptions bordered layout='vertical'>
                        <Descriptions.Item style={{ width: 200 }} label={t('budget')}>
                            {editMode && rolesEditBudget.includes(+getUser().role) ? <InputNumber style={{ width: '100%' }} value={budget} onBlur={onChangeBudget} /> : formatCurrency(budget)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('balance')}>{formatCurrency(analytic.totalBalance)}</Descriptions.Item>
                    </Descriptions>
                </div>
                <Table
                    components={components}
                    rowClassName={() => cx('editable-row')}
                    size="small"
                    columns={columns.map(item => {
                        if (item.children && item.children.some(child => child.children)) {
                            return {
                                ...item,
                                children: item.children.map(child => ({
                                    ...child,
                                    children: (child?.children || []).filter(subChild => !subChild.isHide)
                                }))
                            }
                        }

                        return item;
                    })}
                    bordered
                    dataSource={dataSource}
                    pagination={false}
                    sticky
                    summary={(pageData) => {
                        return (
                            <Table.Summary fixed='bottom'>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={2}>
                                        <strong>{t('sum')}</strong>
                                    </Table.Summary.Cell>
                                    {/* <Table.Summary.Cell align='right'>
                                        <strong>{formatCurrency(analytic.totalNoonShift)}</strong>
                                    </Table.Summary.Cell> */}
                                    <Table.Summary.Cell align='right' >
                                        <strong>{formatCurrency(analytic.produceShop)}</strong>
                                    </Table.Summary.Cell>
                                    {
                                        revenueInfo.cabinet > 0 &&
                                        <Table.Summary.Cell align='right'>
                                            <strong>{formatCurrency(analytic.produceCabin1)}</strong>
                                        </Table.Summary.Cell>
                                    }
                                    {
                                        revenueInfo.cabinet > 1 &&
                                        <Table.Summary.Cell align='right'>
                                            <strong>{formatCurrency(analytic.produceCabin2)}</strong>
                                        </Table.Summary.Cell>
                                    }
                                    <Table.Summary.Cell align='right' >
                                        <strong>{formatCurrency(analytic.revenueShop)}</strong>
                                    </Table.Summary.Cell>
                                    {
                                        revenueInfo.cabinet > 0 &&
                                        <Table.Summary.Cell align='right'>
                                            <strong>{formatCurrency(analytic.revenueCabin1)}</strong>
                                        </Table.Summary.Cell>
                                    }
                                    {
                                        revenueInfo.cabinet > 1 &&
                                        <Table.Summary.Cell align='right'>
                                            <strong>{formatCurrency(analytic.revenueCabin2)}</strong>
                                        </Table.Summary.Cell>
                                    }
                                    <Table.Summary.Cell align='right' >
                                        <strong>{formatCurrency(analytic.totalSuperMarket)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{formatCurrency(analytic.totalQsl)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align='right' >
                                        <strong>{formatCurrency(analytic.totalCityFood)}</strong>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
                <br />
            </Card>
            <br />
            <Card className={cx('summary')} title={t('summary')}>
                <Descriptions bordered column={1}>
                    <Descriptions.Item label={t('numOfWork')}>
                        {analytic.numberOfWork}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('netRevenue')}>
                        {formatCurrency(analytic.netRevenue)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('averageNetRevenueByDay')}>
                        {formatCurrency(analytic.averageRevenue)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('%qsl')}>
                        {formatPercent(analytic.qslPercent)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('%cityFoodSuperMarket')}>
                        {formatPercent(analytic.cityFoodSuperMarket)}
                    </Descriptions.Item>
                    <Descriptions.Item className={cx('order', { over: analytic.orderPercent * 100 > 26 })} label={t('%order')}>
                        {formatPercent(analytic.orderPercent)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('note')}>
                        {editMode && rolesEditNote.includes(+getUser().role) ?
                            <Input.TextArea defaultValue={note} onBlur={onChangeNote} /> :
                            <strong><em dangerouslySetInnerHTML={{ __html: renderHtmlTextBreakLine(note) }}></em></strong>}
                    </Descriptions.Item>
                    <Descriptions.Item className={cx('order', { over: analytic.growth * 100 < 0 })} label={t('growth')}>
                        {formatPercent(analytic.growth)}
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        </>
    )
}